"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Check as CheckCircle2, Clock, PhoneCall, Search, ShieldCheck, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyHighlights = [{
  icon: Search,
  title: "Non-Invasive Detection",
  description: "Acoustic and thermal diagnostics pinpoint hidden leaks without wall destruction."
}, {
  icon: Clock,
  title: "24/7 Rapid Response",
  description: "Emergency plumbers dispatch immediately across Jonesboro and surrounding areas."
}, {
  icon: ShieldCheck,
  title: "Upfront Fixed Pricing",
  description: "Clear diagnostics and upfront quotes before any repair work begins."
}];
export default function LeakHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerAnimation = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="leakdetectionhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Background Subtle Accent Gradients */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden">
        <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-32 left-10 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          
          {/* Main Hero Copy */}
          <motion.div variants={containerAnimation} initial="hidden" animate="show" className="flex flex-col items-start lg:col-span-7">
            <motion.div variants={itemAnimation} className="mb-4">
              <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <Waves className="mr-1.5 size-3.5 text-primary" />
                Emergency Leak Detection & Repair
              </Badge>
            </motion.div>

            <motion.h1 variants={itemAnimation} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl">
              Stop Hidden Water Damage Fast in Jonesboro
            </motion.h1>

            <motion.p variants={itemAnimation} className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Unexplained spikes in your water bill, damp spots on floors, or running water sounds behind walls? 
              Our licensed plumbers use advanced acoustic sensors and thermal scanning to locate and fix hidden water leaks today.
            </motion.p>

            {/* Quick Proof Badges */}
            <motion.div variants={itemAnimation} className="mt-6 flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-chart-4" />
                <span>Slab & Underground Leaks</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-chart-4" />
                <span>Pipe Burst Repairs</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-chart-4" />
                <span>Jonesboro, GA Local Experts</span>
              </div>
            </motion.div>

            {/* Primary Action Group */}
            <motion.div variants={itemAnimation} className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground shadow-md hover:bg-primary/90">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2 font-semibold">
                  <PhoneCall className="size-4" />
                  Call Now: (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-background text-foreground hover:bg-muted">
                <Link to="/contact/" className="inline-flex items-center justify-center font-medium">
                  Schedule Online Inspection
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemAnimation} className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-block size-2 rounded-full bg-chart-4 animate-pulse" />
              <span>Available 24 hours a day, 7 days a week for plumbing emergencies</span>
            </motion.div>
          </motion.div>

          {/* Right Visual & Diagnostic Info Card */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Media Container */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                <div className="relative h-64 sm:h-72 w-full overflow-hidden bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_technician_inspection.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_technician_inspection.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/leak_detection_technician_inspection.webp-wvc-sizes" alt="Plumbing technician detecting water leak in residential wall" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  
                  {/* Overlay Urgent Warning Chip */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-lg bg-background/95 p-3 shadow-md backdrop-blur-xs">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                        <AlertTriangle className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Active Leak Suspected?</p>
                        <p className="text-[11px] text-muted-foreground">Turn off main shutoff valve immediately.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Highlights Grid */}
                <div className="p-5 sm:p-6">
                  <h2 className="font-default text-base font-semibold text-card-foreground">
                    Our Leak Detection Protocol
                  </h2>
                  <div className="mt-4 space-y-4">
                    {keyHighlights.map((item, i) => {
                    const IconComponent = item.icon;
                    return <div key={item.title} data-index={i} className="flex items-start gap-3">
                          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                            <IconComponent className="size-4 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-card-foreground">{item.title}</h3>
                            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{item.description}</p>
                          </div>
                        </div>;
                  })}
                  </div>
                </div>
              </div>

              {/* Floating Reassurance Badge */}
              <Card className="absolute -bottom-6 -left-4 hidden sm:flex items-center gap-3 border border-border bg-popover/95 p-3.5 shadow-xl backdrop-blur-xs">
                <CardContent className="p-0 flex items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold text-sm">
                    24h
                  </div>
                  <div>
                    <p className="text-xs font-bold text-popover-foreground">Average Response: Under 45 Mins</p>
                    <p className="text-[11px] text-muted-foreground">Jonesboro & Clayton County Service</p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

        </div>
      </div>
    </section>;
}